import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const SecuritySetupForm = ({ formData, errors, onChange, onCheckboxChange }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordRequirements = [
    { met: formData?.password?.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/?.test(formData?.password), text: 'One uppercase letter' },
    { met: /[a-z]/?.test(formData?.password), text: 'One lowercase letter' },
    { met: /[0-9]/?.test(formData?.password), text: 'One number' },
    { met: /[!@#$%^&*]/?.test(formData?.password), text: 'One special character (!@#$%^&*)' }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="Shield" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-foreground truncate">Security Setup</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">Create a secure password</p>
        </div>
      </div>

      <div className="bg-muted/50 rounded-lg p-3 sm:p-4">
        <p className="text-xs sm:text-sm font-medium text-foreground mb-2">Password Requirements:</p>
        <ul className="text-xs text-muted-foreground space-y-1.5">
          <li className="flex items-start gap-2">
            <Icon 
              name={formData?.password?.length >= 8 ? "CheckCircle2" : "Circle"} 
              size={14} 
              className="mt-0.5 flex-shrink-0"
              color={formData?.password?.length >= 8 ? "var(--color-success)" : "var(--color-muted-foreground)"} 
            />
            <span>At least 8 characters</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon 
              name={/[A-Z]/?.test(formData?.password) ? "CheckCircle2" : "Circle"} 
              size={14}
              className="mt-0.5 flex-shrink-0"
              color={/[A-Z]/?.test(formData?.password) ? "var(--color-success)" : "var(--color-muted-foreground)"} 
            />
            <span>One uppercase letter</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon 
              name={/[a-z]/?.test(formData?.password) ? "CheckCircle2" : "Circle"} 
              size={14}
              className="mt-0.5 flex-shrink-0"
              color={/[a-z]/?.test(formData?.password) ? "var(--color-success)" : "var(--color-muted-foreground)"} 
            />
            <span>One lowercase letter</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon 
              name={/\d/?.test(formData?.password) ? "CheckCircle2" : "Circle"} 
              size={14}
              className="mt-0.5 flex-shrink-0"
              color={/\d/?.test(formData?.password) ? "var(--color-success)" : "var(--color-muted-foreground)"} 
            />
            <span>One number</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon 
              name={/[!@#$%^&*]/?.test(formData?.password) ? "CheckCircle2" : "Circle"} 
              size={14}
              className="mt-0.5 flex-shrink-0"
              color={/[!@#$%^&*]/?.test(formData?.password) ? "var(--color-success)" : "var(--color-muted-foreground)"} 
            />
            <span>One special character (!@#$%^&*)</span>
          </li>
        </ul>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={onChange}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 p-1 hover:bg-muted rounded transition-clinical"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            placeholder="Re-enter your password"
            value={formData?.confirmPassword}
            onChange={onChange}
            error={errors?.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 p-1 hover:bg-muted rounded transition-clinical"
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="space-y-3 pt-4 border-t border-border">
          <Checkbox
            label="I agree to the Terms of Service and Privacy Policy"
            checked={formData?.agreeToTerms}
            onChange={(e) => onCheckboxChange('agreeToTerms', e?.target?.checked)}
            error={errors?.agreeToTerms}
            required
          />

          <Checkbox
            label="I consent to receive important account notifications via email"
            checked={formData?.agreeToNotifications}
            onChange={(e) => onCheckboxChange('agreeToNotifications', e?.target?.checked)}
          />

          <Checkbox
            label="I certify that all information provided is accurate and truthful"
            checked={formData?.certifyAccuracy}
            onChange={(e) => onCheckboxChange('certifyAccuracy', e?.target?.checked)}
            error={errors?.certifyAccuracy}
            required
          />
        </div>
      </div>
    </div>
  );
};

export default SecuritySetupForm;